import React, { useMemo } from 'react';
import { TableModal } from './Modal';

// Clients Distribution data type
export interface ClientsDistributionData {
    client: string;
    pitstop: string;
    active: number;
    percentageTotal: number;
}

// Clients Distribution Modal Props
interface ClientsDistributionModalProps {
    isOpen: boolean;
    onClose: () => void;
    data: ClientsDistributionData[];
    loading?: boolean;
    hasMore?: boolean;
    onLoadMore?: () => void;
}

// Sort field enum for type safety
enum ClientsDistributionSortField {
    CLIENT = 'client',
    PITSTOP = 'pitstop',
    ACTIVE = 'active',
    PERCENTAGE = 'percentageTotal'
}

// Column configuration
const COLUMN_CONFIG = {
    [ClientsDistributionSortField.CLIENT]: {
        title: 'Client',
        sortable: true,
        render: (value: string) => (
            <span className="font-medium text-neutral-900 text-sm leading-5" style={{ letterSpacing: '-0.006em' }}>
                {value}
            </span>
        )
    },
    [ClientsDistributionSortField.PITSTOP]: {
        title: 'Pitstop',
        sortable: true,
        render: (value: string) => (
            <span className="font-medium text-neutral-900 text-sm leading-5" style={{ letterSpacing: '-0.006em' }}>
                {value}
            </span>
        )
    },
    [ClientsDistributionSortField.ACTIVE]: {
        title: 'Active',
        sortable: true,
        render: (value: number) => (
            <span 
                className="font-normal tabular-nums text-sm leading-5" 
                style={{ letterSpacing: '-0.006em', color: '#5C5C5C' }}
            >
                {value.toLocaleString('en-US')}
            </span>
        )
    },
    [ClientsDistributionSortField.PERCENTAGE]: {
        title: '% of Total Vehicle',
        sortable: true,
        render: (value: number) => (
            <span 
                className="font-normal tabular-nums text-sm leading-5" 
                style={{ letterSpacing: '-0.006em', color: '#5C5C5C' }}
            >
                {value}%
            </span>
        )
    }
};

// Column order for consistent display and CSV export
const COLUMN_ORDER = [
    ClientsDistributionSortField.CLIENT,
    ClientsDistributionSortField.PITSTOP,
    ClientsDistributionSortField.ACTIVE,
    ClientsDistributionSortField.PERCENTAGE
];

const ClientsDistributionModal: React.FC<ClientsDistributionModalProps> = ({
    isOpen,
    onClose,
    data,
    loading = false,
    hasMore = false,
    onLoadMore
}) => {
    // Generate columns from configuration
    const columns = useMemo(() => {
        return COLUMN_ORDER.map(field => ({
            key: field,
            title: COLUMN_CONFIG[field].title,
            sortable: COLUMN_CONFIG[field].sortable,
            render: COLUMN_CONFIG[field].render
        }));
    }, []);

    // Sort function
    const handleSort = (data: ClientsDistributionData[], field: string, direction: 'asc' | 'desc') => {
        return [...data].sort((a, b) => {
            let aValue = a[field as keyof ClientsDistributionData];
            let bValue = b[field as keyof ClientsDistributionData];

            // Handle different data types
            if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = (bValue as string).toLowerCase();
            }

            if (aValue < bValue) {
                return direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    // Get column order for CSV export
    const getColumnOrder = () => COLUMN_ORDER;

    // Get CSV value for a specific field
    const getCsvValue = (row: ClientsDistributionData, field: string): string | number => {
        switch (field as ClientsDistributionSortField) {
            case ClientsDistributionSortField.CLIENT:
                return row.client;
            case ClientsDistributionSortField.PITSTOP:
                return row.pitstop;
            case ClientsDistributionSortField.ACTIVE:
                return row.active;
            case ClientsDistributionSortField.PERCENTAGE:
                return `${row.percentageTotal}%`;
            default:
                return '';
        }
    };

    return (
        <TableModal<ClientsDistributionData>
            isOpen={isOpen}
            onClose={onClose}
            title="Clients by Active EV Detail"
            modalTitle="Clients by Active EV"
            description="View client activity volume by pitstop location."
            data={data}
            columns={columns}
            sortConfig={{
                defaultField: ClientsDistributionSortField.ACTIVE,
                defaultDirection: 'desc'
            }}
            csvFileName="clients_by_active_ev.csv"
            onSort={handleSort}
            getColumnOrder={getColumnOrder}
            getCsvValue={getCsvValue}
            loading={loading}
            hasMore={hasMore}
            onLoadMore={onLoadMore}
        />
    );
};

export default ClientsDistributionModal;